import { useState, useEffect, useCallback } from 'react';
import { StreamingMetadata, ViewerError, UseStreamingMetadata } from '../types/viewer.types';
import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService, roleService, contentService, tagService, searchService, authService } from '../../services';

/**
 * Custom hook for fetching and managing streaming metadata
 */
export const useStreamingMetadata = (
  contentId: string,
  teamId?: string,
  initialMetadata?: StreamingMetadata
): UseStreamingMetadata => {
  const [metadata, setMetadata] = useState<StreamingMetadata | null>(initialMetadata || null);
  const [loading, setLoading] = useState<boolean>(!initialMetadata);
  const [error, setError] = useState<ViewerError | null>(null);

  // Helper to determine content type from MIME type
  const getContentTypeFromMime = useCallback((mimeType: string): StreamingMetadata['contentType'] => {
    if (!mimeType) return 'unknown';
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    if (mimeType === 'application/pdf') return 'document';
    if (mimeType.startsWith('text/')) {
      if (mimeType === 'text/html') return 'html';
      if (mimeType === 'text/markdown' || mimeType === 'text/x-markdown') return 'markdown';
      return 'text';
    }
    if (mimeType.includes('office') || mimeType.includes('word') ||
        mimeType.includes('excel') || mimeType.includes('powerpoint') ||
        mimeType.includes('opendocument')) return 'document';
    return 'unknown';
  }, []);

  const fetchMetadata = useCallback(async () => {
    if (!contentId) return;

    setLoading(true);
    setError(null);

    try {
      const metadataResponse = await contentService.getContentStreamingMetadata(contentId, teamId);

      // Extract metadata from the response - includes new download strategy
      const metadataMap = metadataResponse.metadata || {};
      const mimeType = metadataResponse.mimeType || 'application/octet-stream';

      // Transform service response to match StreamingMetadata interface
      const normalizedMetadata: StreamingMetadata = {
        contentId: metadataResponse.contentId || contentId,
        fileName: metadataResponse.fileName || 'Unknown',
        mimeType,
        fileSize: metadataResponse.fileSize || 0,
        contentType: getContentTypeFromMime(mimeType), // Determine from MIME type
        canStream: metadataResponse.canStream ?? false, // Default to false - prefer direct downloads
        canConvert: true, // Default to true for conversion capability
        availableFormats: ['pdf'], // Default available formats
        mediaInfo: undefined, // mediaInfo not available in ContentStreamingMetadata
        metadata: metadataMap, // Include the full metadata map with download strategy
        // Add new fields for direct download support
        presignedUrl: metadataMap.presigned_url,
        downloadStrategy: (metadataMap.download_strategy || 'direct') as 'direct' | 'stream',
        downloadMethod: (metadataMap.download_method || 'single') as 'single' | 'chunked',
        recommendedChunkSize: metadataMap.recommended_chunk_size ? parseInt(metadataMap.recommended_chunk_size) : undefined,
        supportsRange: metadataMap.supports_range === 'true',
        isLargeFile: metadataMap.is_large_file === 'true'
      };

      setMetadata(normalizedMetadata);

    } catch (err: any) {
      console.error('Failed to fetch streaming metadata:', err);

      const viewerError: ViewerError = {
        code: err.response?.status === 404 ? 'FILE_NOT_FOUND' :
              err.response?.status === 403 ? 'PERMISSION_DENIED' :
              err.name === 'NetworkError' ? 'NETWORK_ERROR' : 'STREAMING_ERROR',
        message: err.message || 'Failed to load file metadata',
        details: err.response?.data,
        retryable: err.response?.status !== 403 && err.response?.status !== 404
      };

      setError(viewerError);
    } finally {
      setLoading(false);
    }
  }, [contentId, teamId, getContentTypeFromMime]);

  const refetch = useCallback(async () => {
    await fetchMetadata();
  }, [fetchMetadata]);

  useEffect(() => {
    if (initialMetadata) {
      // Update metadata when initialMetadata changes (e.g., when presigned URL loads)
      setMetadata(initialMetadata);
      setLoading(false);
    } else {
      // Fetch metadata from API if no initial metadata provided
      fetchMetadata();
    }
  }, [fetchMetadata, initialMetadata, contentId]);

  return {
    metadata,
    loading,
    error,
    refetch
  };
};